import {
  Box,
  Button,
  IconButton,
  Typography,
  Tooltip
} from "@mui/material";
import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import apiRoutes from "../../../store/apiConfigs";
import DataGridComponent from "../../../components/DataGridComponent";
import { eventAssignedObjectListApi, reseteventAssignedObjectList } from "../../../store/event-module/assignedObjectsListing";
import { eventObjectsPublishApi } from "../../../store/event-module/assignObjectToEventPublish";
import { unAssignEventObjectsApi } from "../../../store/event-module/unAssignObjectFromChannel";
import CustomGridToolbar from "../../../components/CustomGridToolbarWithoutDownload";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import RemoveIcon from "@mui/icons-material/Remove";
import { AuthContext } from "../../../context/AuthContext";
import PermissionGate from "../../../components/PermissionGate";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function EventAssignedModuleList({ getRowSpacing, NoRowsOverlay, channelCategory, event_id, callModulelistList, openExternalLink, closeExternalLink }) {
  const { result: assignedObjectListResult, loading: assignedObjectLoading, total: assignedObjectTotal } = useSelector((state) => state?.eventAssignedObject);

  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const [queryOptionsChangeAssignedModule, setQueryOptionsChangeAssignedModule] = useState({});
  const [paginationModelChangeAssignedModule, setPaginationModelChangeAssignedModule] = useState({
    page: 0,
    pageSize: 20,
  });

  const paginationModelAssignedMemoized = useMemo(() => paginationModelChangeAssignedModule, [paginationModelChangeAssignedModule]);

  const onFilterChangeAssignedModule = useCallback((filterModel) => {
    setQueryOptionsChangeAssignedModule({ filterModel: { ...filterModel } });
  }, []);

  const createQueryParams = useCallback((paginationModel, queryOptions) => {
    const filterModel = queryOptions?.filterModel;
    const items = filterModel?.items;
    const firstItem = items && items.length > 0 ? items[0] : {};
    const quickFilterValues = filterModel?.quickFilterValues;

    return {
      page: paginationModel?.page + 1,
      page_size: paginationModel?.pageSize,
      field: firstItem.field || '',
      operator: firstItem.operator || (quickFilterValues && "contains") || '',
      search: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || '',
      value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || '',
      title: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || '',
      event_id,
      content_type: String(channelCategory).toLocaleLowerCase(),
    };
  }, [event_id, channelCategory]);

  const callObjectList = useCallback(() => {
    const queryAssignedModule = createQueryParams(paginationModelChangeAssignedModule, queryOptionsChangeAssignedModule);
    if (queryAssignedModule.content_type === "polls") {
      queryAssignedModule.content_type = "sundaypolls";
    } else if (queryAssignedModule.content_type.includes("courses")) {
      queryAssignedModule.content_type = "course";
    }
    dispatch(eventAssignedObjectListApi(queryAssignedModule));
  }, [createQueryParams, dispatch, paginationModelChangeAssignedModule, queryOptionsChangeAssignedModule])

  const handleRemoveFromChannel = async (record) => {
    dispatch(unAssignEventObjectsApi({
      ID: record.id
    })).then((action) => {
      if (!action.type.includes("rejected")) {
        callObjectList()
        callModulelistList()
      }
    }).catch(err => console.log(err))
  }

  const handlePublishUnpublishChannel = async (record) => {
    dispatch(eventObjectsPublishApi({
      id: record.row.id,
      is_published: !record.row.is_published,
      content_type: record.row.content_type,
      event: record.row.event,
      object_id: record.row.object_id,
    })).then((action) => {
      if (!action.type.includes("rejected")) {
        callObjectList()
      }
    }).catch(err => console.log(err))
  }

  const handlePaginationModelChangeAssigned = (model) => {
    if (
      paginationModelChangeAssignedModule.page !== model.page ||
      paginationModelChangeAssignedModule.pageSize !== model.pageSize
    ) {
      setPaginationModelChangeAssignedModule(model);
    }
  };

  // Fetch assigned module data
  useEffect(() => {
    callObjectList()
  }, [dispatch, paginationModelAssignedMemoized, queryOptionsChangeAssignedModule, callObjectList, channelCategory]);

  const assignedObjectListColumn = [
    {
      field: "title",
      headerName: "Title",
      width: 450,
      flex: 1,
      filterable: true,
      sortable: false,
      hideable: false,
      renderCell: (data) => (<>
        {data.row.object_data.title}
      </>)
    },
    {
      field: "content_type",
      headerName: "Content Type",
      width: 200,
      flex: 1,
      filterable: false,
      sortable: false,
      hideable: false
    },
    {
      field: "actions",
      headerName: "Actions",
      filterable: false,
      sortable: false,
      width: 300,
      disableExport: true,
      renderCell: (data) => (
        <Box display={"flex"} justifyItems={"center"} alignItems={"center"} justifyContent={"space-around"} width={"100%"}>
          <PermissionGate module="event" action={data?.row?.is_published ? "unpublish" : "publish"}>
            <Button color="success" variant="contained"
              endIcon={data?.row?.is_published ? <CheckIcon /> : <ArrowRightIcon />}
              onClick={() => handlePublishUnpublishChannel(data)}
            >
              {data?.row?.is_published ? "UnPublish" : "Publish"}
            </Button>
          </PermissionGate>
          <PermissionGate module="event" action="update">
            {data?.row?.content_type === "externallink" && (
              <Tooltip title={"edit"}>
                <IconButton
                  onClick={() => openExternalLink(data?.row)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </PermissionGate>
          <PermissionGate module="event" action="delete">
            <Button
              color="info"
              endIcon={<RemoveIcon />}
              label="Available"
              variant="outlined"
              onClick={() => handleRemoveFromChannel(data.row)}
            >
              Remove
            </Button>
          </PermissionGate>


        </Box>
      ),
    },
  ];
  return (
    <>
      <DataGridComponent
        loading={assignedObjectLoading}
        rowCount={assignedObjectTotal}
        pageSize={paginationModelChangeAssignedModule?.pageSize}
        onPageSizeChange={(newPageSize) => {
          setPaginationModelChangeAssignedModule({
            ...paginationModelChangeAssignedModule,
            pageSize: newPageSize,
          })
        }}
        paginationModel={paginationModelAssignedMemoized}
        onPaginationModelChange={handlePaginationModelChangeAssigned}
        getRowId={(row) => `${row.id}-${row.module_id}-${row.module_type}-${uuidv4()}`}
        rows={assignedObjectListResult}
        getRowSpacing={getRowSpacing}
        columns={assignedObjectListColumn}
        onFilterModelChange={onFilterChangeAssignedModule}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          toolbar: (props) =>
          (CustomGridToolbar({
            ...props, moduleType: "assignedObject", queryOptions: queryOptionsChangeAssignedModule, paginationModel: paginationModelChangeAssignedModule, apiUrl: apiRoutes.channelAction('assign-object/list')
          })),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
    </>
  );
}
